/**
 * Bulk status hooks for the management tree: latest measurement time and
 * open-alert summary per installation, each in a single request.
 */

import { useQuery } from '@tanstack/react-query';
import { measurementsService, alertsService } from 'src/api/services';
import { queryKeys } from './queryKeys';

export interface AlertSummaryEntry {
  worst_severity: string | null;
  count: number;
  types: string[];
}

const STATUS_POLL_MS = Number(import.meta.env.VITE_POLLING_INTERVAL) || 300000;

/** Map of installationId -> ISO timestamp of its latest measurement. */
export function useLatestTimesQuery() {
  return useQuery<Record<string, string>>({
    queryKey: queryKeys.measurements.latestTimes(),
    queryFn: () => measurementsService.getLatestTimes(),
    staleTime: 60 * 1000,
    refetchInterval: STATUS_POLL_MS,
  });
}

/** Map of installationId -> open-alert summary (worst severity + counts). */
export function useAlertSummaryQuery() {
  return useQuery<Record<string, AlertSummaryEntry>>({
    queryKey: queryKeys.alerts.summary(),
    queryFn: () => alertsService.getSummary(),
    staleTime: 60 * 1000,
    refetchInterval: STATUS_POLL_MS,
  });
}

export interface CabinetHealth {
  schaltschranknummer: string | null;
  last_heartbeat: string | null;
  age_seconds: number | null;
  stale: boolean;
  main_power_ok: boolean | null;
  mains_power_ok: boolean | null;
  internet_ok: boolean | null;
  autossh_ok: boolean | null;
  system_healthy: boolean | null;
}

export interface StationHealth {
  installation_id: string;
  available: boolean;
  cabinets: CabinetHealth[];
}

/**
 * Per-cabinet station health (power / network) for the selected installation.
 * Polls the light backend endpoint; `available=false` => render "unknown".
 */
export function useStationHealthQuery(installationId: string | null | undefined) {
  return useQuery<StationHealth>({
    queryKey: queryKeys.measurements.stationHealth(installationId ?? ''),
    queryFn: () => measurementsService.getStationHealth(installationId as string),
    enabled: !!installationId,
    staleTime: 60 * 1000,
    refetchInterval: STATUS_POLL_MS,
  });
}
